// Dépôt d'une réclamation (H7-03) : catégorie, description, dossier concerné (facultatif), case
// « réclamation sensible » expliquée (cochée d'office pour les catégories toujours sensibles),
// pièces du coffre ; confirmation avec le numéro REC.
import { CATEGORIES_RECLAMATION, CATEGORIES_SENSIBLES, type CategorieReclamation } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Formulaire, LienBouton, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { requete } from '../../../api/client';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { useCoffre, useMesDossiers } from '../api';
import { useCollectiviteChoisie, useCollectivitesPubliques } from '../collectivite';
import { Bloc, EnTeteCitoyen, ExigeConnexion } from '../commun';
import type { ReclamationUsager } from '../types';
import styles from '../citoyen.module.css';
import depot from '../depot/depot.module.css';

function Depot({ code }: { code: string }) {
  const collectivites = useCollectivitesPubliques();
  const dossiers = useMesDossiers();
  const coffre = useCoffre();
  const [collectivite, setCollectivite] = useState(code);
  const [categorie, setCategorie] = useState<CategorieReclamation | ''>('');
  const [description, setDescription] = useState('');
  const [dossierId, setDossierId] = useState('');
  const [sensible, setSensible] = useState(false);
  const [pieces, setPieces] = useState<string[]>([]);
  const [locales, setLocales] = useState<{ champ: string; message: string }[]>([]);
  const [deposee, setDeposee] = useState<ReclamationUsager | null>(null);
  const toujoursSensible = categorie !== '' && CATEGORIES_SENSIBLES.includes(categorie);
  const envoi = useAction({
    executer: () =>
      requete<ReclamationUsager>('/usager/reclamations', {
        methode: 'POST',
        corps: {
          collectivite,
          categorie,
          description: description.trim(),
          ...(dossierId ? { objet_lie_type: 'dossier', objet_lie_id: dossierId } : {}),
          sensible: sensible || toujoursSensible,
          pieces_coffre: pieces.map((piece_id) => ({ piece_id })),
        },
      }),
    apres: (r) => setDeposee(r),
  });
  const erreurs = [...locales, ...envoi.erreursChamps.map((e) => ({ champ: `rec-${e.champ.replace(/_/g, '-')}`, message: e.message }))];
  const erreurChamp = (c: string) => erreurs.find((e) => e.champ === c)?.message ?? null;
  const dossiersCollectivite = (dossiers.data ?? []).filter((d) => d.collectivite.code === collectivite && d.statut.code !== 'brouillon');

  if (deposee) {
    return (
      <div className={styles.pile}>
        <Bloc etiquette="Réclamation déposée" titre={deposee.categorie_libelle}>
          <span className={`${styles.mono} ${styles.blocTitre}`}>{deposee.numero}</span>
          <p className={styles.blocTexte}>
            {deposee.collectivite.nom} s&apos;engage à répondre{deposee.echeance ? ` avant le ${date(deposee.echeance)}` : ' sous quinze jours ouvrés'}.
            {deposee.sensible ? ' Réclamation sensible : seules les personnes habilitées de la collectivité peuvent la lire.' : ''}
          </p>
        </Bloc>
        <div>
          <LienBouton href="/citoyen/reclamations" variante="principal">
            Suivre mes réclamations
          </LienBouton>
        </div>
      </div>
    );
  }
  return (
    <Formulaire
      libelle="Nouvelle réclamation"
      onSoumettre={() => {
        const l = [!categorie ? { champ: 'rec-categorie', message: 'Choisissez une catégorie' } : null, description.trim().length < 3 ? { champ: 'rec-description', message: 'Décrivez le problème rencontré' } : null].filter(
          (x): x is { champ: string; message: string } => x !== null,
        );
        setLocales(l);
        if (l.length === 0) envoi.lancer(undefined);
      }}
      erreurs={erreurs}
      actions={
        <Bouton type="submit" chargement={envoi.enCours} libelleChargement="Envoi en cours" pleineLargeur>
          Déposer la réclamation
        </Bouton>
      }
    >
      {envoi.messageErreur && envoi.erreursChamps.length === 0 ? <Bandeau type="erreur">{envoi.messageErreur}</Bandeau> : null}
      {(collectivites.data ?? []).length > 1 ? (
        <Selection id="rec-collectivite" libelle="Collectivité concernée" options={(collectivites.data ?? []).map((c) => ({ valeur: c.code, libelle: c.nom }))} value={collectivite} onChange={(e) => setCollectivite(e.target.value)} />
      ) : null}
      <Selection
        id="rec-categorie"
        libelle="Catégorie"
        optionVide="Choisir"
        options={Object.entries(CATEGORIES_RECLAMATION).map(([valeur, libelle]) => ({ valeur, libelle }))}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value as CategorieReclamation | '')}
        erreur={erreurChamp('rec-categorie')}
        required
      />
      <ZoneTexte id="rec-description" libelle="Ce qui s'est passé" value={description} onChange={(e) => setDescription(e.target.value)} erreur={erreurChamp('rec-description')} required rows={5} maxLength={10000} aide="Faits, date, lieu, personnes ou service concernés" />
      {dossiersCollectivite.length > 0 ? (
        <Selection id="rec-dossier" libelle="Demande concernée" optionVide="Aucune demande en particulier" options={dossiersCollectivite.map((d) => ({ valeur: d.id, libelle: `${d.numero ?? 'Brouillon'} · ${d.demarche.libelle}` }))} value={dossierId} onChange={(e) => setDossierId(e.target.value)} facultatif />
      ) : null}
      <fieldset className={depot.groupe}>
        <legend>Réclamation sensible</legend>
        <CaseACocher libelle="Cette réclamation est sensible" checked={sensible || toujoursSensible} disabled={toujoursSensible} onChange={(e) => setSensible(e.target.checked)} />
        <p className={depot.aide}>
          Une réclamation sensible met en cause une personne (comportement d&apos;un agent), allègue une corruption ou concerne des données personnelles. Elle n&apos;est lue que par les personnes
          habilitées de la collectivité, jamais affichée sur une carte, et ses pièces sont confidentielles.
          {toujoursSensible ? ' La catégorie choisie est toujours traitée comme sensible.' : ''}
        </p>
      </fieldset>
      {(coffre.data ?? []).length > 0 ? (
        <fieldset className={depot.groupe}>
          <legend>Pièces du coffre à joindre</legend>
          {(coffre.data ?? [])
            .filter((p) => !p.expiree)
            .map((p) => (
              <CaseACocher key={p.id} libelle={p.titre} checked={pieces.includes(p.id)} onChange={(e) => setPieces(e.target.checked ? [...pieces, p.id] : pieces.filter((x) => x !== p.id))} />
            ))}
        </fieldset>
      ) : null}
    </Formulaire>
  );
}

export function PageNouvelleReclamation() {
  const [choisie] = useCollectiviteChoisie();
  if (!choisie) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <EnTeteCitoyen titre="Déposer une réclamation" retour="/citoyen/reclamations" libelleRetour="Réclamations" />
      <ExigeConnexion titre="Connectez-vous pour déposer une réclamation">
        <Depot code={choisie.code} />
      </ExigeConnexion>
    </>
  );
}
